import { useCallback, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCaretLeft } from '@fortawesome/free-solid-svg-icons/faSquareCaretLeft';
import { ReducedParams } from '@api/order-reporting/types/ReducedParams';
import { ReducedMaterialsGridData } from '@pages/reporting-pages/reduced-materials/types/ReducedMaterialsGridData';
import useOrderClick from '@hooks/reportings/useOrderClick';
import { useHasPermission } from '@hooks/has-permission/useHasPermission';
import { AppRolesEnum } from '@features/routing/appRolesEnum';
import { useSaveMaterialCommentMutation } from '@api/order-reporting/commentApi';
import { useSaveMaterialPlanningReasonMutation, useSaveMaterialExecutionReasonMutation } from '@api/order-reporting/reasonApi';
import EditableGridCell from 'components/editable-grid-cell/EditableGridCell';
import { SelectReasonCell } from 'components/select-reason-cell/SelectReasonCell';
import { SelectReasonType } from 'components/select-reason-cell/SelectReasonType';
import { ShowReasonColumn } from '@pages/reporting-pages/ShowReasonColumn';



interface GridRowProps {
	item: ReducedMaterialsGridData,
	rowIndex: number,
	validParams: ReducedParams,
	showReasonColumn: ShowReasonColumn,
}
  

const GridRow = (props: GridRowProps) => {
	const { rowIndex, validParams, showReasonColumn } = props;
	const { materialId, materialClientId, materialName, orderCustomerSapId, name, city, street, salesMan, tlsOperator, comment, orderClientId, distributionType,
		orderUnversionedSystemId, orderRequiredDeliveryDate, orderRequiredDeliveryDateISO, orderExecutionPlace, shipmentDistributionCode, countReducedBy, volumeReducedBy,
		orderPlanningReasonName, orderExecutionReasonName,
		orderOriginalVolume, orderSentVolume, orderDeliveredVolume, orderedMinusSentVolume, sentMinusDeliveredVolume,
		orderOriginalCount, orderSentCount, orderDeliveredCount, orderedMinusSentCount, sentMinusDeliveredCount } = props.item;

	const hasPermission = useHasPermission();
	const orderClick = useOrderClick({ date: orderRequiredDeliveryDateISO, orderUnvId: orderUnversionedSystemId });
	const [ saveComment, { data, isError } ] = useSaveMaterialCommentMutation();
	const [ savePlanningReason, { data: prData, isError: prIsError } ] = useSaveMaterialPlanningReasonMutation();
	const [ saveExecutionReason, { data: erData, isError: erIsError } ] = useSaveMaterialExecutionReasonMutation();

	const getClassName = useCallback((centered: boolean, leftAlign: boolean = false) => 
		`app-grid-cell${
			rowIndex % 2 == 0 ? ' secondary-cell' : ' primary-cell'
		}${
			leftAlign ? ' left-align-cell' : ''
		}${
			centered ? ' centered-cell' : ''
		}`
	,[ rowIndex ]);


	return (
		<>
			<div className={getClassName(true)}>
				{hasPermission(AppRolesEnum.orders_Read) &&
					<FontAwesomeIcon className='order-link-icon' icon={faSquareCaretLeft} onClick={orderClick} />}
			</div>

			<div className={getClassName(true)}>
				<p>{materialClientId ? materialClientId : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{materialName ? materialName : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderCustomerSapId ? orderCustomerSapId : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{name ? name : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{city ? city : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{street ? street : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{salesMan ? salesMan : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{tlsOperator ? tlsOperator : '-'}</p>
			</div>

			<EditableGridCell
				requiredRoles={AppRolesEnum.materialReports_Modify}
				className={getClassName(true)}
				isApiError={data && !data.successfull ? true : isError}
				externalValue={comment}
				onPressSave={(value: string) => saveComment({
					unversionedOrderSystemId: orderUnversionedSystemId,
					materialSystemId: materialId,
					comment: value,
					originalArgs: validParams
				})}
			/>

			{(showReasonColumn === ShowReasonColumn.planning || showReasonColumn === ShowReasonColumn.planningAndExecution) &&
				<SelectReasonCell
					type={SelectReasonType.planningReason}
					requiredRoles={AppRolesEnum.orderReports_Modify}
					className={getClassName(true)}
					isApiError={prData && !prData.successfull ? true : prIsError}
					value={orderPlanningReasonName}
					onChange={(reason: { id: string, name: string }) => savePlanningReason({
						unversionedOrderSystemId: orderUnversionedSystemId,
						materialSystemId: materialId,
						reasonCode: reason.id,
						reasonCodeName: reason.name,
						originalArgs: validParams
					})}
				/>}

			{(showReasonColumn === ShowReasonColumn.execution || showReasonColumn === ShowReasonColumn.planningAndExecution) &&
				<SelectReasonCell
					type={SelectReasonType.executionReason}
					requiredRoles={AppRolesEnum.orderReports_Modify}
					className={getClassName(true)}
					isApiError={erData && !erData.successfull ? true : erIsError}
					value={orderExecutionReasonName}
					onChange={(reason: { id: string, name: string }) => saveExecutionReason({
						unversionedOrderSystemId: orderUnversionedSystemId,
						materialSystemId: materialId,
						reasonCode: reason.id,
						reasonCodeName: reason.name,
						originalArgs: validParams
					})}
				/>}

			<div className={getClassName(true)}>
				<p>{orderClientId ? orderClientId : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderRequiredDeliveryDate ? orderRequiredDeliveryDate : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderExecutionPlace ? orderExecutionPlace : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{distributionType ? distributionType : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{shipmentDistributionCode ? shipmentDistributionCode : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{countReducedBy ? countReducedBy : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{volumeReducedBy ? volumeReducedBy : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderOriginalCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderSentCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderDeliveredCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderOriginalVolume}</p>
			</div>
			
			<div className={getClassName(true)}>
				<p>{orderSentVolume}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderDeliveredVolume}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderedMinusSentCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{sentMinusDeliveredCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderedMinusSentVolume}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{sentMinusDeliveredVolume}</p>
			</div>
		</>
	)
}

export default memo(GridRow);